import { Lfa_Service_Acceptance_Status } from '~/graphql/types';
import type { ServicesFeedbackType } from '../../../../state/servicesFeedbackState';
import type {
  FeedbackInputValidationType,
  ValidationError,
  ValidationState,
} from '../../../../state/validationState';
import validateCriteria from '../validateCriteria';
import validateSnoozeData from '../validateSnoozeData';

const text = {
  selectDate: 'Selecteer een datum',
  invalidDate: 'Ongeldige datum geselecteerd',
  pastDate: 'De gekozen datum mag niet in het verleden liggen',
  selectRescheduleDate: 'Selecteer een datum voor herschikking',
};

const parseAndValidateDate = (
  dateStr?: string,
  timeStr?: string,
): string | null => {
  if (!dateStr) return text.selectDate;

  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours = 0, minutes = 0] = timeStr
    ? timeStr.split(':').map(Number)
    : [];
  const selectedDate = new Date(year, month - 1, day, hours, minutes);

  if (isNaN(selectedDate.getTime())) return text.invalidDate;
  if (selectedDate < new Date()) return text.pastDate;

  return null;
};

export const validateFeedback = (
  servicesFeedback: ServicesFeedbackType,
): ValidationState => {
  const state = new Map<FeedbackInputValidationType, Array<ValidationError>>();
  if (servicesFeedback?.Event_Feedback_Done) {
    const servicesErrors: Array<ValidationError | null> =
      servicesFeedback.Event_Feedback_Done.services.map(service => {
        if (
          !(
            service.acceptance[0].status ===
            Lfa_Service_Acceptance_Status.Rejected
          )
        ) {
          return null;
        }

        const validated = validateCriteria(
          service.acceptance[0].rejectionCriteria ?? [],
        );

        if (!validated) {
          return null;
        }

        return {
          serviceId: service.id,
          field: validated.field,
          message: validated?.message,
        };
      });

    const filteredServicesErrors = servicesErrors.filter(
      error => error !== null,
    ) as Array<ValidationError>;

    state.set('Done', filteredServicesErrors);
  }

  if (servicesFeedback?.Event_Feedback_Appointment_Rescheduled) {
    const appointmentDate =
      servicesFeedback.Event_Feedback_Appointment_Rescheduled.appointmentDate;

    if (
      !appointmentDate ||
      (!appointmentDate.Day && !appointmentDate.DayTime)
    ) {
      state.set('Rescheduled', [
        {
          field: 'rescheduledDate',
          message: text.selectRescheduleDate,
        },
      ]);
    } else {
      const { Day, DayTime } =
        servicesFeedback.Event_Feedback_Appointment_Rescheduled
          .appointmentDate || {};

      const dateStr = DayTime?.d || Day?.d;
      const timeStr = DayTime?.t;

      const validationMessage = parseAndValidateDate(dateStr, timeStr);

      if (validationMessage) {
        state.set('Rescheduled', [
          { field: 'rescheduledDate', message: validationMessage },
        ]);
      }
    }
  }

  if (servicesFeedback?.Event_Feedback_Appointment_Failed) {
    const rejectionCriteria =
      servicesFeedback.Event_Feedback_Appointment_Failed.rejectionCriteria;

    const validated = validateCriteria(rejectionCriteria);

    if (validated) {
      state.set('Failed', [validated]);
    }
  }

  if (servicesFeedback?.Event_Feedback_Snooze) {
    const snoozeUntil = servicesFeedback.Event_Feedback_Snooze.snoozedUntil;
    const snoozeNote = servicesFeedback.Event_Feedback_Snooze.note;

    const snoozeErrors = validateSnoozeData(snoozeUntil, snoozeNote);

    if (snoozeErrors.length > 0) {
      state.set('Snooze', snoozeErrors);
    }
  }

  return state;
};

export default validateFeedback;
